
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEvaluation } from "./useEvaluation";

export const useAutoSave = (
  content: string,
  wordCount: number,
  draftId: string | null,
  userId: string | undefined,
  topicId: string,
  setDraftId: (id: string) => void
) => {
  const lastSaveRef = useRef<string>(content);
  const { data: evaluation } = useEvaluation(draftId);
  const lastSaveTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Don't auto-save if the draft has been evaluated
    if (evaluation) {
      console.log("[AutoSave] Draft is evaluated, skipping auto-save");
      return;
    }

    if (!userId || !topicId) {
      console.log("[AutoSave] Missing required fields, skipping auto-save");
      return;
    }

    const autoSave = async () => {
      const now = Date.now();
      // Only save if content has changed since last save and it's been at least 30 seconds
      if (!content.trim() || 
          (content === lastSaveRef.current && 
           now - lastSaveTimeRef.current < 30000)) {
        console.log("[AutoSave] No changes to save or too soon");
        return;
      }

      console.log("[AutoSave] Starting auto-save...");

      try {
        if (draftId) {
          console.log("[AutoSave] Updating existing draft:", draftId);
          const { error } = await supabase
            .from("drafts")
            .update({
              content,
              word_count: wordCount,
              updated_at: new Date().toISOString(),
            })
            .eq("id", draftId);

          if (error) throw error;
        } else {
          console.log("[AutoSave] Creating new draft");
          const { data, error } = await supabase
            .from("drafts")
            .insert({
              user_id: userId,
              topic_id: topicId,
              content,
              word_count: wordCount,
            })
            .select()
            .single();

          if (error) throw error;
          setDraftId(data.id);
        }

        lastSaveRef.current = content;
        lastSaveTimeRef.current = now;
        toast.success("Draft auto-saved");
        console.log("[AutoSave] Save successful");
      } catch (error) {
        console.error("[AutoSave] Save failed:", error);
        toast.error("Failed to save draft");
      }
    };

    const interval = setInterval(autoSave, 30000);
    return () => clearInterval(interval);
  }, [content, draftId, userId, topicId, wordCount, setDraftId, evaluation]);
};
